<template>
  <div v-if="isSessionEstablishing">
    <div class="session-establishment-wrapper">
      <div class="header-wrapper">
        <h1 class="header-title">Voice Agent</h1>
        <div class="loading-spinner-text-wrapper">
          <div class="loading-ring">
            <div></div><div></div><div></div><div></div>
          </div>
          <div class="session-description">
            Please wait while we establish connection with the agent.
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="showEstablishmentError">
    <div class="session-establishment-wrapper">
      <div class="header-wrapper">
        <h1 class="header-title">Voice Agent</h1>
        <div class="loading-spinner-text-wrapper">
          <div class="session-description">
            We are currently facing issues while establishing connection with the agent. Please try again later.
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="application-wrapper">
    <div class="application-container">

      <div v-if="chatHistory.length === 0" class="chat-landing-screen">
        <div class="landing-page-container">
          <div class="header-wrapper">
            <h1 class="header-title">Voice Agent</h1>
            <p class="header-description">
              Hi, I'm Voice Agent. Speak or type to start a conversation.
            </p>
          </div>
          <div class="user-input-wrapper">
            <div class="content-wrapper">
              <textarea
                ref="textarea"
                @keydown.enter.prevent="sendTextMessage"
                @input="resizeTextArea"
                v-model="prompt"
                placeholder="Message Voice Agent"
                class="input-text-area"
              />
              <img
                src="../assets/zs-icon-plane-send.svg"
                @click="sendTextMessage"
                class="send-msg-icon"
              />
              <img
                v-if="!isAudioRecording"
                src="../assets/micOff.png"
                class="img-speaker-icon"
                @click="startRecording"
              />
              <img
                v-if="isAudioRecording"
                src="../assets/microphoneRed.png"
                class="img-speaker-icon"
                @click="stopRecordingPrompt"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="user-assistant-conversation-wrapper" v-else>
        <div class="field-assistant-header-wrapper">
          <h1 class="chatbox-header">Voice Agent</h1>
        </div>
        <div ref="chatbox" id="chatbox">
          <div
            v-for="(chat, index) in chatHistory"
            :key="index"
            style="transition: all 0.3s ease-in-out"
          >
            <div v-if="chat.user">
              <div class="user-message-wrapper">
                <div class="user-message-content">
                  <div class="message-content-wrapper">
                    <div class="timestamp-content">
                      {{ formattedTime }}
                      <span
                        v-if="chat.inputType"
                        class="input-type-badge"
                        :class="'input-type-' + chat.inputType"
                      >
                        {{ chat.inputType === "text" ? "Text" : "Voice" }}
                      </span>
                    </div>
                    {{ chat.message }}
                  </div>
                  <img src="@/assets/userIcon.png" class="img-user-icon" />
                </div>
              </div>
            </div>

            <div v-else>
              <div class="assistant-message-wrapper">
                <div class="assistant-message-content">
                  <img src="@/assets/assistantIcon.png" class="img-assistant-icon" />
                  <div class="message-content">
                    <div class="message-header">
                      <span class="assistant-name">Voice Agent</span>
                      <span class="timestamp">{{ formattedTime }}</span>
                    </div>
                    <div v-html="formatHtml(chat.message)"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-input-wrapper">
          <textarea
            ref="textareaChat"
            v-model="prompt"
            @input="resizeTextArea"
            @keydown.enter.prevent="sendTextMessage"
            placeholder="Message Voice Agent"
            class="input-text-area"
          />
          <img
            src="../assets/zs-icon-plane-send.svg"
            @click="sendTextMessage"
            class="send-msg-icon-input"
          />
          <img
            v-if="!isAudioRecording"
            src="../assets/micOff.png"
            class="img-speaker-icon"
            @click="startRecording"
          />
          <img
            v-if="isAudioRecording"
            src="../assets/microphoneRed.png"
            class="img-speaker-icon"
            @click="stopRecordingPrompt"
          />
        </div>
      </div>

    </div>

    <div v-if="confirmAbort" class="toast-wrapper">
      <div class="toast-wrapper-content">
        <span class="close-popup-button" @click="confirmAbort = false">&times;</span>
        <br />
        <div style="text-align: center">
          <img class="success-tick-sign" src="@/assets/warning.jpg" />
        </div>
        <br />
        <div style="text-align: center">
          <strong>Are you sure you want to stop the voice session?</strong>
        </div>
        <div class="abort-toast-button-wrapper">
          <button class="schedule-btn" @click="stopStreaming">Yes</button>
          <button class="schedule-btn" @click="confirmAbort = false">No</button>
        </div>
        <br />
      </div>
    </div>
  </div>
</template>

<script>
import { AudioPlayer } from "../../public/static/AudioPlayer.js";

const audioPlayer = new AudioPlayer();

import microPhoneActivate from "@/assets/point-smooth-beep-230573.mp3";
import microphoneOff from "@/assets/bottle-205353.mp3";

// Override with VUE_APP_BACKEND_WS_URL in .env.local (e.g.
// wss://your-domain.com/ws once the backend is deployed) — defaults to
// local dev against pipecat_agent running on its default port.
const BACKEND_WS_URL = process.env.VUE_APP_BACKEND_WS_URL || "ws://localhost:8081/ws";
// The AudioWorklet module is served by the same backend (proxied at
// /worklets/*) — derive its http(s) URL from BACKEND_WS_URL instead of
// hardcoding a host, so this doesn't silently point at localhost in
// deployed environments (ws->http, wss->https, same host/port).
const BACKEND_WORKLET_URL =
  BACKEND_WS_URL.replace(/^ws/, "http").replace(/\/ws$/, "") +
  "/worklets/audio-player.worklet.js";

export default {
  data() {
    return {
      isSessionEstablishing: true,
      showEstablishmentError: false,
      sessionId: "",
      ws: null,
      chatHistory: [],
      prompt: "",
      isAudioRecording: false,
      confirmAbort: false,
      audioStream: null,
      audioContext: null,
      sourceNode: null,
      processor: null,
      TARGET_SAMPLE_RATE: 16000,
      stream_ready: false,
    };
  },
  computed: {
    formattedTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
  mounted() {
    this.initAudio();
    this.connectWebSocket();
  },
  beforeUnmount() {
    if (this.ws) this.ws.close();
  },
  methods: {
    formatHtml(text) {
      return (text || "").replace(/\n/g, "<br>");
    },
    resizeTextArea() {
      const el = this.$refs.textarea || this.$refs.textareaChat;
      if (el) {
        el.style.height = "auto";
        el.style.height = el.scrollHeight + "px";
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatBox = this.$refs.chatbox;
        if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
      });
    },
    connectWebSocket() {
      this.ws = new WebSocket(BACKEND_WS_URL);

      this.ws.onopen = () => {
        const msg = { type: "sessionEstablish", data: {} };
        this.ws.send(JSON.stringify(msg));
      };

      this.ws.onmessage = (e) => {
        const event = JSON.parse(e.data);

        if (event.type === "customEvent") {
          if (event.subType === "SESSION_ESTABLISHED") {
            this.sessionId = event.sessionId || "";
            if (this.sessionId) {
              this.isSessionEstablishing = false;
            } else {
              this.showEstablishmentError = true;
              this.isSessionEstablishing = false;
            }
          } else if (event.subType === "VOICE_CONNECTION_ESTABLISHED") {
            this.ws.send(JSON.stringify({ type: "startAudioRecording" }));
            this.startStreaming();
          } else if (event.subType === "TEXT_OUTPUT") {
            const { content, role } = event.data;
            if (role === "USER") {
              if (this.chatHistory.length && this.chatHistory[this.chatHistory.length - 1].user) {
                this.chatHistory[this.chatHistory.length - 1].message += ` ${content}`;
              } else {
                this.chatHistory.push({ user: true, message: content, inputType: "audio" });
              }
            } else {
              if (
                this.chatHistory.length &&
                !this.chatHistory[this.chatHistory.length - 1].user
              ) {
                const last = this.chatHistory[this.chatHistory.length - 1];
                if (!last.message.includes(content)) {
                  last.message += `${content}\n`;
                }
              } else {
                this.chatHistory.push({ user: false, message: content });
              }
            }
            this.scrollToBottom();
          } else if (event.subType === "AUDIO_OUTPUT") {
            this.playSonicAudio(event.data);
          } else if (event.subType === "BARGE_IN") {
            // Server-side interruption only drops audio not yet sent over
            // the socket — flush whatever's already queued/playing in the
            // AudioWorklet so the bot's trailing voice doesn't bleed into
            // the mic over the customer's first words.
            audioPlayer.bargeIn();
          } else if (event.subType === "SESSION_DEACTIVATED") {
            this.stream_ready = false;
          }
        }
      };

      this.ws.onerror = () => {
        this.isSessionEstablishing = false;
        this.showEstablishmentError = true;
      };

      this.ws.onclose = () => {
        this.stream_ready = false;
      };
    },
    async initAudio() {
      audioPlayer.start(BACKEND_WORKLET_URL);
    },
    base64ToFloat32Array(base64String) {
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++)
        bytes[i] = binaryString.charCodeAt(i);
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++)
        float32Array[i] = int16Array[i] / 32768.0;
      return float32Array;
    },
    async playSonicAudio(base64Audio) {
      if (this.isAudioRecording) {
        const audioData = this.base64ToFloat32Array(base64Audio);
        audioPlayer.playAudio(audioData);
      }
    },
    arrayBufferToBase64(buffer) {
      const binary = [];
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++)
        binary.push(String.fromCharCode(bytes[i]));
      return btoa(binary.join(""));
    },
    sendVoiceChunk(audioData) {
      const pcmData = new Int16Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        const s = Math.max(-1, Math.min(1, audioData[i]));
        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: "audioChunk",
          data: this.arrayBufferToBase64(pcmData.buffer),
        }));
      }
    },
    startRecording() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "start", data: { sessionId: this.sessionId } }));
      }
    },
    async startStreaming() {
      this.isAudioRecording = true;
      this.playSound(microPhoneActivate);
      try {
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: { ideal: this.TARGET_SAMPLE_RATE },
          },
        });
        this.audioContext = new AudioContext({ sampleRate: this.TARGET_SAMPLE_RATE });
        if (!audioPlayer.initialized) await audioPlayer.start(BACKEND_WORKLET_URL);
        this.sourceNode = this.audioContext.createMediaStreamSource(this.audioStream);
        this.processor = this.audioContext.createScriptProcessor(512, 1, 1);
        this.processor.onaudioprocess = (e) => {
          this.sendVoiceChunk(e.inputBuffer.getChannelData(0));
        };
        this.sourceNode.connect(this.processor);
        this.processor.connect(this.audioContext.destination);
      } catch (error) {
        this.isAudioRecording = false;
        if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
          alert("Microphone permission denied! Please allow microphone access.");
        }
      }
    },
    stopRecordingPrompt() {
      if (!this.isAudioRecording) return;
      this.confirmAbort = true;
    },
    stopStreaming() {
      try {
        if (this.processor) this.processor.disconnect();
        if (this.sourceNode) this.sourceNode.disconnect();
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: "endAudio" }));
          this.ws.send(JSON.stringify({ type: "stop" }));
        }
        this.playSound(microphoneOff);
        this.chatHistory = [];
        this.isAudioRecording = false;
        this.confirmAbort = false;
      } catch (error) {
        console.error("Error stopping voice recording:", error);
      }
    },
    sendTextMessage() {
      const text = this.prompt.trim();
      if (!text) return;
      this.chatHistory.push({ user: true, message: text, inputType: "text" });
      this.prompt = "";
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "textInput", text }));
      }
      this.scrollToBottom();
    },
    playSound(src) {
      const audio = new Audio(src);
      audio.play().catch(() => {});
    },
  },
};
</script>

<style scoped>
.session-establishment-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  left: 50%;
  top: 50%;
  width: 80%;
  transform: translate(-50%, -50%);
  gap: 1rem;
}

.loading-spinner-text-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
}

.session-description {
  font-size: 20px;
}

.loading-ring {
  color: #465565;
}

.loading-ring,
.loading-ring div {
  box-sizing: border-box;
}

.loading-ring {
  display: inline-block;
  position: relative;
  width: 20px;
  height: 20px;
  margin-right: 16px;
}

.loading-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  height: 20px;
  width: 20px;
  border: 3px solid currentColor;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: currentColor transparent transparent transparent;
}

.loading-ring div:nth-child(1) { animation-delay: -0.45s; }
.loading-ring div:nth-child(2) { animation-delay: -0.3s; }
.loading-ring div:nth-child(3) { animation-delay: -0.15s; }

@keyframes lds-ring {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.application-wrapper {
  width: 100%;
  display: flex;
  height: 100vh;
  background: linear-gradient(to bottom, white, #f4f7ff);
}

.application-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.chat-landing-screen {
  flex: 1;
  padding: 20px;
  position: relative;
  width: 100%;
}

.landing-page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  left: 50%;
  top: 50%;
  width: 80%;
  transform: translate(-50%, -50%);
  gap: 1rem;
}

.header-wrapper {
  text-align: center;
  width: 100%;
  font-family: sans-serif;
}

.header-title {
  font-size: 2.5em;
  font-weight: 700;
  background: linear-gradient(to right, #fd9b36, #e54725);
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  margin-bottom: 10px;
}

.header-description {
  margin: 0 auto;
  color: #1a1628;
  width: 70%;
  font-weight: 600;
}

.user-input-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}

.content-wrapper {
  width: 75%;
  position: relative;
}

.input-text-area {
  width: 100%;
  border-radius: 3rem;
  max-height: 5rem;
  overflow-y: auto;
  resize: none;
  padding: 0.7rem 5rem 0.7rem 2rem;
  height: 3rem;
  border: 1px solid #f68131;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}

.input-text-area::-webkit-scrollbar { display: none; }
.input-text-area:focus { border-color: orange; outline: orange; }

.send-msg-icon {
  height: 20px;
  position: absolute;
  right: 3%;
  top: 1.5rem;
  transform: translate(0, -50%);
  cursor: pointer;
}

.send-msg-icon-input {
  height: 20px;
  position: absolute;
  right: 1.5%;
  top: 1.5rem;
  transform: translate(0, -50%);
  cursor: pointer;
}

.img-speaker-icon {
  cursor: pointer;
  transition: opacity 0.3s;
  height: 25px;
  position: absolute;
  right: 3.2rem;
  top: 1.4rem;
}

.user-assistant-conversation-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 20px;
  position: relative;
}

.chatbox-header {
  font-size: 2em;
  font-weight: 700;
  background: linear-gradient(to right, #fd9b36, #e54725);
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  margin-bottom: 10px;
}

#chatbox {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 5rem;
  padding-bottom: 1rem;
}

.chat-input-wrapper {
  width: 100%;
  position: relative;
  display: flex;
  bottom: 6.5rem;
  justify-content: center;
}

.chat-input-wrapper .input-text-area {
  width: 75%;
}

.user-message-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 1rem;
}

.user-message-content {
  width: 45%;
  display: flex;
  margin-right: 2rem;
}

.message-content-wrapper {
  background-color: #e1eaff;
  padding: 10px;
  border-radius: 10px;
  width: 100%;
  text-align: left;
  word-wrap: break-word;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.img-user-icon {
  height: 30px;
  margin-left: 10px;
  margin-top: 0.3rem;
}

.timestamp-content {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  margin-right: 10px;
  width: 100%;
  color: #999;
  margin-bottom: 0.2rem;
}

.input-type-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.input-type-text {
  background-color: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}

.input-type-audio {
  background-color: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.assistant-message-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.assistant-message-content {
  display: flex;
  flex-direction: row;
  max-width: 100%;
}

.img-assistant-icon {
  height: 30px;
  margin-right: 5px;
  margin-top: 0.3rem;
}

.message-content {
  background-color: white;
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 10px;
  max-width: 100%;
  width: 95%;
  text-align: left;
  word-wrap: break-word;
  box-shadow: 0 2px 0px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  margin-right: 2rem;
}

.message-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
}

.assistant-name {
  font-weight: bold;
  background: linear-gradient(to right, #fd9b36, #e54725);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.timestamp {
  color: #999;
  margin-left: 10px;
}

.toast-wrapper {
  z-index: 2;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-wrapper-content {
  background: #fff;
  border-radius: 8px;
  padding: 0.5rem;
  position: relative;
  width: 30vw;
}

.close-popup-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.success-tick-sign {
  height: 4rem;
  width: 4rem;
  display: inline-block;
  border-radius: 50%;
}

.abort-toast-button-wrapper {
  display: flex;
  margin: 20px 0;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  column-gap: 20px;
}

.schedule-btn {
  white-space: nowrap;
  border: none;
  border-radius: 5px;
  padding: 10px 15px;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: rgb(253, 155, 54);
  color: white;
  font-weight: 500;
}
</style>

